<template>
  <div class="pa-0 ma-0 fill-height admin-saved-searches" fluid>
    <v-dialog
      v-model="overlay"
      hide-overlay
      persistent
      width="300"
      lazy
    >
      <v-card color="secondary" dark>
        <v-card-text>
          {{ overlayMessage }}
          <v-progress-linear
            indeterminate
            color="white"
            class="mb-0"
          />
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="copyToast.show"
      color="success"
      top
      :timeout="3000"
    >
      {{ copyToast.message }}
      <template v-slot:action="{ attrs }">
        <v-btn
          text
          v-bind="attrs"
          @click="copyToast.show = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <div v-if="!overlay">
      <v-row justify="center" class="px-0 py-0">
        <v-col cols="12">
          <v-card color="#f5f5f5" flat tile max-width="1400" class="mx-auto">
            <p
              style="text-align:center;"
              class="pt-6 display-1 font-weight-medium"
            >
              Saved Search Management
            </p>
            <p style="text-align:center;" class="body-1 font-weight-normal">
              Search users, review their saved searches, and copy a saved search to another user.
            </p>

            <v-row v-if="error" class="mx-3">
              <v-col cols="12">
                <v-alert type="error" dismissible @input="error = null">
                  <strong>Error:</strong> {{ error }}
                </v-alert>
              </v-col>
            </v-row>

            <v-row v-else-if="!isAdmin" class="mx-3">
              <v-col cols="12">
                <v-alert type="warning" outlined>
                  Admin access is required to use saved search management.
                </v-alert>
              </v-col>
            </v-row>

            <div v-else class="mx-3 mb-6">
              <v-card class="mb-6" elevation="1">
                <v-card-title class="py-3">
                  <v-icon left color="primary">mdi-account-search</v-icon>
                  Step 1: Select Users
                </v-card-title>
                <v-card-text class="pt-0">
                  <v-row>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="sourceEmailDomainFilter"
                        :items="filteredSourceDomains"
                        label="Filter Source Users by Email Domain"
                        placeholder="Type to search domains..."
                        prepend-icon="mdi-email"
                        clearable
                        outlined
                        dense
                        @input="filterSourceUsers"
                        @change="filterSourceUsers"
                        @update:search-input="filterSourceDomains"
                        class="mb-3"
                      >
                        <template slot="item" slot-scope="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>{{ item }}</v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-autocomplete
                        v-model="targetEmailDomainFilter"
                        :items="filteredTargetDomains"
                        label="Filter Target Users by Email Domain"
                        placeholder="Type to search domains..."
                        prepend-icon="mdi-email"
                        clearable
                        outlined
                        dense
                        @input="filterTargetUsers"
                        @change="filterTargetUsers"
                        @update:search-input="filterTargetDomains"
                        class="mb-3"
                      >
                        <template slot="item" slot-scope="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>{{ item }}</v-list-item-title>
                          </v-list-item-content>
                        </template>
                      </v-autocomplete>
                    </v-col>
                  </v-row>

                  <v-row class="mt-2">
                    <v-col cols="12" md="6">
                      <v-select
                        v-model="selectedSourceUserId"
                        :items="filteredSourceUsers"
                        item-text="email"
                        item-value="user_id"
                        label="Select Source User"
                        prepend-icon="mdi-account"
                        outlined
                        dense
                        clearable
                        :loading="loadingUsers"
                        :disabled="!sourceEmailDomainFilter"
                        @change="onSourceUserChange"
                      >
                        <template slot="item" slot-scope="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>{{ item.email || item.user_id }}</v-list-item-title>
                            <v-list-item-subtitle>
                              {{ formatUserSubtitle(item) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-select>
                    </v-col>

                    <v-col cols="12" md="6">
                      <v-select
                        v-model="selectedTargetUserId"
                        :items="filteredTargetUsers"
                        item-text="email"
                        item-value="user_id"
                        label="Select Target User"
                        prepend-icon="mdi-account-multiple"
                        outlined
                        dense
                        clearable
                        :loading="loadingUsers"
                        :disabled="!targetEmailDomainFilter"
                      >
                        <template slot="item" slot-scope="{ item }">
                          <v-list-item-content>
                            <v-list-item-title>{{ item.email || item.user_id }}</v-list-item-title>
                            <v-list-item-subtitle>
                              {{ formatUserSubtitle(item) }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </template>
                      </v-select>
                    </v-col>
                  </v-row>

                  <div class="caption grey--text text--darken-1">
                    Source users: {{ filteredSourceUsers.length }} | Target users: {{ filteredTargetUsers.length }}
                  </div>
                </v-card-text>
              </v-card>

              <v-card class="mb-6" elevation="1" v-if="selectedSourceUserId">
                <v-card-title class="py-3">
                  <v-icon left color="primary">mdi-content-save-search</v-icon>
                  Step 2: Review Source User Saved Searches
                </v-card-title>
                <v-card-text class="pt-0">
                  <div v-if="sourceUserDetails" class="mb-4">
                    <div class="subtitle-1 font-weight-medium">
                      {{ sourceUserDetails.full_name || sourceUserDetails.email || sourceUserDetails.user_id }}
                    </div>
                    <div class="body-2 grey--text text--darken-1">
                      {{ sourceUserDetails.email }}<span v-if="sourceUserDetails.organisation"> | {{ sourceUserDetails.organisation }}</span>
                    </div>
                  </div>

                  <v-data-table
                    :headers="savedSearchHeaders"
                    :items="savedSearches"
                    :loading="loadingSavedSearches"
                    item-key="id"
                    class="elevation-1"
                  >
                    <template slot="item.name" slot-scope="{ item }">
                      <div class="font-weight-medium">{{ item.name || item.search_query?.search_name || "Untitled saved search" }}</div>
                    </template>

                    <template slot="item.plain_text_query" slot-scope="{ item }">
                      <span>{{ shortenText(item.plain_text_query) }}</span>
                    </template>

                    <template slot="item.search_type" slot-scope="{ item }">
                      <v-chip small color="info" text-color="white">
                        {{ item.search_type || "unknown" }}
                      </v-chip>
                    </template>

                    <template slot="item.notify" slot-scope="{ item }">
                      <v-chip
                        small
                        :color="item.notify ? 'success' : 'grey'"
                        text-color="white"
                      >
                        {{ item.notify ? "On" : "Off" }}
                      </v-chip>
                    </template>

                    <template slot="item.new_matches" slot-scope="{ item }">
                      <v-chip
                        small
                        :color="item.new_matches > 0 ? 'warning' : 'grey'"
                        :text-color="item.new_matches > 0 ? 'black' : 'white'"
                      >
                        {{ item.new_matches || 0 }}
                      </v-chip>
                    </template>

                    <template slot="item.created" slot-scope="{ item }">
                      {{ formatDate(item.created) }}
                    </template>

                    <template slot="item.action" slot-scope="{ item }">
                      <v-btn
                        small
                        color="primary"
                        :loading="copyingSearchId === item.id"
                        :disabled="!canCopyToTarget(item)"
                        @click="copySelectedSavedSearch(item)"
                      >
                        Copy to Target User
                      </v-btn>
                    </template>
                  </v-data-table>
                </v-card-text>
              </v-card>

              <v-card v-if="lastCopyResult" elevation="1">
                <v-card-title class="py-3">
                  <v-icon left color="success">mdi-check-circle</v-icon>
                  Last Copy Result
                </v-card-title>
                <v-card-text class="pt-0">
                  <div class="subtitle-1 font-weight-medium mb-2">
                    {{ lastCopyResult.saved_search?.name || "Saved search copied successfully" }}
                  </div>
                  <div class="body-2">
                    Target user:
                    {{ lastCopyResult.target_user?.email || lastCopyResult.target_user?.user_id || selectedTargetUserId }}
                  </div>
                  <div class="body-2">
                    Search type: {{ lastCopyResult.saved_search?.search_type || "-" }}
                  </div>
                </v-card-text>
              </v-card>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import { getCurrentUserProfile } from "@/components/APIManager.js";
import {
  getUserSavedSearches,
  copySavedSearch,
} from "@/components/AdminAPIManager.js";
import AdminUserSelectionMixin from "@/mixins/AdminUserSelectionMixin.js";
import { formatAdminDateTime } from "@/utils/adminDateTime.js";

export default {
  name: "SavedSearchAdministration",
  mixins: [AdminUserSelectionMixin],

  data() {
    return {
      overlay: true,
      overlayMessage: "Loading saved search management...",
      error: null,
      isAdmin: false,
      loadingSavedSearches: false,
      copyingSearchId: null,
      sourceUserDetails: null,
      savedSearches: [],
      lastCopyResult: null,
      copyToast: {
        show: false,
        message: "",
      },
      savedSearchHeaders: [
        { text: "Name", value: "name", width: "18%" },
        { text: "Query", value: "plain_text_query", width: "32%" },
        { text: "Type", value: "search_type", width: "10%" },
        { text: "Alerts", value: "notify", width: "8%" },
        { text: "New Matches", value: "new_matches", width: "10%" },
        { text: "Created", value: "created", width: "12%" },
        { text: "Action", value: "action", sortable: false, width: "10%" },
      ],
    };
  },

  async mounted() {
    await this.initializePage();
  },

  methods: {
    async initializePage() {
      try {
        const response = await getCurrentUserProfile();
        if (response.status === "success") {
          this.isAdmin = response.data.tt_admin === true;
        } else {
          this.error = "Failed to load current user.";
        }

        if (this.isAdmin) {
          await this.loadUsers();
          await this.loadDomains();
        }
      } catch (error) {
        this.error = "Failed to load saved search management.";
        this.isAdmin = false;
      } finally {
        this.overlay = false;
      }
    },

    async onSourceUserChange() {
      this.lastCopyResult = null;

      if (!this.selectedSourceUserId) {
        this.sourceUserDetails = null;
        this.savedSearches = [];
        return;
      }

      await this.loadSavedSearches();
    },

    async loadSavedSearches() {
      try {
        this.loadingSavedSearches = true;
        this.error = null;

        const response = await getUserSavedSearches(this.selectedSourceUserId);
        if (response.status !== "success") {
          throw new Error("Failed to load saved searches");
        }

        this.sourceUserDetails = response.data.user || this.findUserById(this.selectedSourceUserId);
        this.savedSearches = response.data.saved_searches || [];
      } catch (error) {
        this.error = "Failed to load user saved searches. Please try again.";
        this.savedSearches = [];
      } finally {
        this.loadingSavedSearches = false;
      }
    },

    async copySelectedSavedSearch(savedSearch) {
      if (!this.canCopyToTarget(savedSearch)) {
        return;
      }

      try {
        this.copyingSearchId = savedSearch.id;
        this.error = null;

        const response = await copySavedSearch(savedSearch.id, this.selectedTargetUserId);
        if (response.status !== "success") {
          throw new Error("Failed to copy saved search");
        }

        this.lastCopyResult = response.data;
        this.copyToast.message = `Copied "${response.data.saved_search?.name || savedSearch.name || "saved search"}" successfully.`;
        this.copyToast.show = true;
      } catch (error) {
        this.error = "Failed to copy saved search. Please try again.";
      } finally {
        this.copyingSearchId = null;
      }
    },

    handleAdminSourceFilterChange() {
      this.lastCopyResult = null;
      this.sourceUserDetails = null;
      this.savedSearches = [];
    },

    handleAdminTargetFilterChange() {
      this.lastCopyResult = null;
    },

    formatDate: formatAdminDateTime,

    shortenText(text, maxLength = 140) {
      if (!text) {
        return "";
      }

      const normalized = String(text).replace(/(<([^>]+)>)/gi, "");
      if (normalized.length <= maxLength) {
        return normalized;
      }

      return `${normalized.slice(0, maxLength).trim()}...`;
    },

    canCopyToTarget(savedSearch) {
      return Boolean(
        savedSearch?.id &&
        this.selectedTargetUserId &&
        this.selectedTargetUserId !== this.selectedSourceUserId
      );
    },
  },
};
</script>

<style scoped>
.admin-saved-searches {
  background-color: #f5f5f5;
}
</style>
